import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getPattern, getProduct, itemPatterns, maxQuantity, unitPrice } from "@/lib/catalog";
import { isSoldOut } from "@/lib/patternAvailability";
import { activePhrases, normalizeName, resolvePhrase } from "@/lib/phrases";

// Carrinho demonstrativo: guardado apenas neste navegador, nada é enviado para servidor.
const KEY = "babybib-demo-cart-v2";
const CartContext = createContext(null);

const load = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "[]");
    return Array.isArray(raw) ? raw.filter((i) => getProduct(i?.slug) && Number.isInteger(i?.qty) && (!getProduct(i.slug).options.length || i.options)) : [];
  } catch {
    return [];
  }
};

const slotKey = (s) => Object.keys(s ?? {}).sort().map((id) => `${id}=${s[id].modelId ?? ""}:${s[id].patternId}`).join(",");
const optKey = (o) => Object.keys(o ?? {}).sort().map((id) => `${id}=${o[id].choiceId}:${o[id].text ?? ""}`).join(",");
const phraseKey = (p) => (p ? `${p.optionId}:${p.articleId ?? ""}:${normalizeName(p.name)}` : "");
export const itemKey = (i) => `${i.slug}|${i.patternId}|${i.personalization ?? ""}|${i.zone ?? ""}|${phraseKey(i.phrase)}|${optKey(i.options)}|${slotKey(i.slots)}|${i.babyCm ?? ""}|${(i.note ?? "").trim()}`;

export function CartProvider({ children }) {
  const [raw, setRaw] = useState(load);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(raw));
  }, [raw]);

  const add = useCallback((entry) => {
    setRaw((prev) => {
      const k = itemKey(entry);
      const limit = maxQuantity(getProduct(entry.slug));
      const found = prev.find((i) => itemKey(i) === k);
      if (found) return prev.map((i) => (itemKey(i) === k ? { ...i, qty: Math.min(i.qty + entry.qty, limit) } : i));
      return [...prev, { ...entry, qty: Math.min(entry.qty, limit) }];
    });
  }, []);

  const setQty = useCallback((k, qty) => {
    setRaw((prev) => prev.map((i) => (itemKey(i) === k ? { ...i, qty: Math.max(1, Math.min(qty, maxQuantity(getProduct(i.slug)))) } : i)));
  }, []);

  const remove = useCallback((k) => setRaw((prev) => prev.filter((i) => itemKey(i) !== k)), []);
  const clear = useCallback(() => setRaw([]), []);

  const value = useMemo(() => {
    const items = raw.map((i) => {
      const product = getProduct(i.slug);
      const unit = unitPrice(product, i.options, i.personalization);
      const patterns = itemPatterns(i);
      // Frase guardada no carrinho: se a opção já não existe (ou a peça passou a pedir uma frase), a linha fica por rever, nunca é substituída em silêncio.
      const phrases = activePhrases(product);
      const res = phrases ? resolvePhrase(phrases, i.phrase) : null;
      const phraseProblem = phrases ? !!res.error : !!i.phrase;
      return { ...i, key: itemKey(i), product, pattern: getPattern(i.patternId), patterns, soldOut: patterns.some(isSoldOut), phraseText: res?.phrase?.text ?? null, phraseProblem, unit, total: unit === null ? null : unit * i.qty };
    });
    const priced = items.filter((i) => i.total !== null);
    return {
      items,
      count: items.reduce((s, i) => s + i.qty, 0),
      subtotal: priced.reduce((s, i) => s + i.total, 0),
      hasUnpriced: items.some((i) => i.total === null),
      // Padrões de todas as peças (para o prazo) e peças cujo padrão esgotou depois de irem para o carrinho.
      patterns: items.flatMap((i) => i.patterns),
      hasSoldOut: items.some((i) => i.soldOut),
      hasPhraseProblem: items.some((i) => i.phraseProblem),
      add,
      setQty,
      remove,
      clear,
      open,
      setOpen,
    };
  }, [raw, add, setQty, remove, clear, open]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => useContext(CartContext);
